import type {
  JobChainTemplateGraph,
  JobChainTemplateMetadata,
  JobChainTemplateIssue,
  ConditionOperator,
} from '@/api/JobChainTemplates';
import type { TFunc } from '@/translations';

export const supportedConditionOperators: ConditionOperator[] = [
  'Equals',
  'NotEquals',
  'GreaterThan',
  'GreaterOrEqual',
  'LessThan',
  'LessOrEqual',
  'Contains',
  'NotContains',
  'StartsWith',
  'EndsWith',
  'In',
  'NotIn',
  'IsNull',
  'IsNotNull',
];

export const graphUnsupportedReasons = (
  graph: JobChainTemplateGraph,
  metadata: JobChainTemplateMetadata,
  t: TFunc,
): string[] => {
  const reasons: string[] = [];
  const checkKeys = (value: object, keys: string[], label: string) =>
    Object.keys(value).forEach((key) => {
      if (!keys.includes(key))
        reasons.push(
          t('jobChainTemplateGraphUnsupportedField', { label, key }),
        );
    });
  checkKeys(graph, ['nodes', 'edges'], t('jobChainTemplateGraphFieldGraph'));
  graph.nodes.forEach((node) => {
    checkKeys(
      node,
      ['key', 'id', 'jobTemplateId', 'joinPolicy', 'deleted'],
      t('jobChainTemplateGraphFieldNode'),
    );
    if (!['AnyLive', 'AllLive'].includes(node.joinPolicy))
      reasons.push(
        t('jobChainTemplateGraphUnsupportedJoinPolicy', {
          joinPolicy: node.joinPolicy,
        }),
      );
    if (!metadata.templateJobs.some((job) => job.id === node.jobTemplateId))
      reasons.push(
        t('jobChainTemplateGraphTemplateJobUnavailable', {
          jobTemplateId: node.jobTemplateId,
        }),
      );
    const job = metadata.templateJobs.find(
      (item) => item.id === node.jobTemplateId,
    );
    if (job?.isSupported === false)
      reasons.push(
        t('jobChainTemplateGraphUnsupportedJobType', { jobType: job.jobType }),
      );
  });
  graph.edges.forEach((edge) => {
    checkKeys(
      edge,
      ['key', 'id', 'fromKey', 'toKey', 'groupId', 'conditions'],
      t('jobChainTemplateGraphFieldEdge'),
    );
    edge.conditions.forEach((condition) => {
      checkKeys(
        condition,
        ['id', 'groupId', 'name', 'condition', 'value'],
        t('jobChainTemplateGraphFieldCondition'),
      );
      const field = metadata.conditionFields.find(
        (item) => item.key === condition.name,
      );
      if (
        !field ||
        field.valueType !== 'string' ||
        !supportedConditionOperators.includes(condition.condition) ||
        field.operators.some(
          (operator) => !supportedConditionOperators.includes(operator),
        ) ||
        !field.operators.includes(condition.condition)
      )
        reasons.push(
          t('jobChainTemplateGraphUnsupportedConditionDefinition', {
            name: condition.name,
            condition: condition.condition,
          }),
        );
    });
  });
  return [...new Set(reasons)];
};

export const resolveIssueTarget = (
  graph: JobChainTemplateGraph,
  issue: JobChainTemplateIssue,
) => {
  const edge = graph.edges.find(
    (item) =>
      item.key === issue.edgeKey ||
      (item.id !== null && `edge-${item.id}` === issue.edgeKey),
  );
  if (edge)
    return {
      key: edge.key,
      elementId:
        issue.conditionIndex !== null
          ? `condition-${edge.key}-${issue.conditionIndex}`
          : `edge-${edge.key}`,
    };
  const node = graph.nodes.find(
    (item) =>
      item.key === issue.nodeKey ||
      (item.id !== null && `node-${item.id}` === issue.nodeKey),
  );
  return node ? { key: node.key, elementId: `node-${node.key}` } : null;
};
